import { MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import { freshHypothesisProblem } from './setup';
import type {
  AddHypothesisAtHalftimeCommand,
  AddHypothesisAtHalftimeSnapshot,
  Decision,
  HalftimeAddOp,
} from './types';

/**
 * HT-2 / PRE-3 — the halftime activities that **mutate the hypothesis set** (build unit U-13b).
 *
 * ── Why this is not in `commands/halftime.ts` ────────────────────────────────
 *
 * That module's whole claim is that neither of its handlers can touch a `Hypothesis`:
 * `HalftimeAdjustmentOp` reaches `talkPoints` only and `MatchNoteOp` reaches `matchNotes` only, which
 * is how HT-6 (*"promoting an adjustment MUST NOT alter counts, statuses, board positions or render
 * order"*) and INV-14 are compile errors rather than review findings. A handler that inserts a
 * hypothesis alongside them would take that shared property away from the module and leave the two
 * op types as the only thing carrying it — true, but no longer legible as one fact about one file.
 *
 * So the seam is **what the activity writes**, the same seam U-13a used: `halftime.ts` writes new
 * artifact rows, this module writes the hypothesis set, and `commands/board.ts` writes positions
 * because INV-4 says positions live in exactly one file.
 *
 * ── Typing is allowed here, and that is a ruling ─────────────────────────────
 *
 * LIVE-9 forbids **requiring** free text *"in an in-play state"*. Halftime is in-match and not
 * in-play (§2.1), and HT-2 says so outright: *"Halftime is not an in-play state, so typing here is
 * unrestricted."* PRE-3 names the two surfaces that get free-text creation — Pre-Match and Halftime —
 * and excludes *"the in-play capture surfaces"*. So this handler accepts a row whose text the user
 * typed, which `handleAddDiscoveryHypothesis` also does structurally but which DISC-5's **sheet**
 * must never offer.
 *
 * The negative that must still hold: text is not *required* to complete any halftime activity. The
 * add sheet also offers the template library and the PRE-4 stub pickers, so PRE-3's free-text control
 * is one of three ways in and never the only one.
 */

const MODULE = 'commands/halftimeManage';

/**
 * PRE-3 / HT-2 — create one hypothesis at halftime, **list-only**.
 *
 * ── The window, and both of its ends ─────────────────────────────────────────
 *
 * `Match.state === 'halftime'`, read inside the transaction. Not `isInMatch`: DISC-5 already owns
 * adding during a half in Discovery, and a second command answering there would be a second set of
 * rules for one surface. Not `fullTime` or later either — §7.4's sheet does not exist after the
 * break, and a hypothesis created then would carry a zero count into distillation as though it had
 * been watched for.
 *
 * **`capturePhase` is deliberately unguarded.** Both phases are inside the window, because §7.4's
 * HT-3 rule is that the HT-2 card is *"added, never substituted"* — a user still in Discovery at
 * halftime reaches this sheet without locking anything first. A `capturePhase === 'locked'` guard
 * here would re-impose, quietly and one layer down, exactly the completion gate §7.6 forbids.
 *
 * ── The row arrives list-only and cannot arrive any other way ────────────────
 *
 * `Decision<HalftimeAddOp>` cannot emit `setBoardPositions`, and `freshHypothesisProblem` refuses a
 * row that already carries a `boardPosition`, a count, an override or a non-`tracking` status. Two
 * mechanisms pointing at the same thing, and neither is redundant: the op type keeps the *handler*
 * from reaching the board, the row check keeps the *caller* from smuggling a position in on the row.
 * Getting onto the board is `SwapBoardTile`, a separate explicit action — LOCK-6's *"no displacement
 * is ever silent"* is only checkable while adding and positioning are two different commands.
 */
export function handleAddHypothesisAtHalftime(
  cmd: AddHypothesisAtHalftimeCommand,
  snap: AddHypothesisAtHalftimeSnapshot,
): Decision<HalftimeAddOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (match.state !== MATCH_STATE.halftime) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_NOT_AT_HALFTIME',
      'Patterns are added here only during the halftime break.',
      { state: match.state },
    );
  }

  if (cmd.row.text.trim() === '') {
    return reject(MODULE, cmd.matchId, 'ERR_HYPOTHESIS_TEXT_REQUIRED', 'That pattern has no text.', {
      id: cmd.row.id,
    });
  }

  const problem = freshHypothesisProblem(cmd.row, cmd.matchId);
  if (problem !== null) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That pattern could not be added because it was malformed.',
      { id: cmd.row.id, problem },
    );
  }

  // Re-adding an id would overwrite a live row — including its count — through a `put`. The snapshot
  // is read inside the transaction, so this is a real check and not a race.
  if (snap.existingIds.includes(cmd.row.id)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_EXISTS',
      'That pattern is already on this match.',
      { id: cmd.row.id },
    );
  }

  return ok({
    ops: [{ t: 'insertHypothesis', row: cmd.row }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'PRE-3: hypothesis added at halftime',
        data: {
          id: cmd.row.id,
          discoveryBucket: cmd.row.discoveryBucket,
          fromTemplate: cmd.row.sourceTemplateId !== null,
        },
      },
    ],
    post: [],
  });
}
